import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PTTButton from './PTTButton';
import { useVoiceChatSession } from '../hooks/useVoiceChatSession';
import { useAudioWorkletRecorder } from '../hooks/useAudioWorkletRecorder';
import { useVoiceOutboxScheduler } from '../hooks/useVoiceOutboxScheduler';

interface Props {
    conversationId: string;
    currentUserId: string;
}

/**
 * VoiceChatInput — the bottom-of-chat input row when voice mode is on.
 * Wraps PTTButton + the recorder hook + the upload session, and surfaces
 * quota/error toasts inline.
 */
const VoiceChatInput: React.FC<Props> = ({ conversationId, currentUserId }) => {
    const { t } = useTranslation('messaging');
    const session = useVoiceChatSession({ conversationId, currentUserId });
    const recorder = useAudioWorkletRecorder();
    useVoiceOutboxScheduler(conversationId, currentUserId);
    const [toast, setToast] = useState<string | null>(null);

    useEffect(() => {
        session.refreshQuota().catch(() => undefined);
        // Pull mic permission upfront. Doing it here — when the user toggles
        // INTO voice mode — keeps the OS permission dialog out of the actual
        // tap-to-record path, which otherwise scrambles iOS pointer state
        // and produces a stream of phantom recordings.
        recorder.prewarm().catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!toast) return;
        const id = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(id);
    }, [toast]);

    const handleComplete = async (frames: Float32Array[], sampleRate: number) => {
        const res = await session.sendFrames(frames, sampleRate);
        if (res === null) {
            // Network failure — recording is safely queued in IDB and the
            // scheduler will retry. Reassure the user instead of suggesting
            // they re-record.
            setToast(t('voice.queuedForRetry', 'No connection — kept your recording, will retry shortly.'));
            return;
        }
        if (!res.ok) {
            if (res.reason === 'inaudible') {
                setToast(t('voice.inaudible', 'Didn\'t catch that — try again'));
            } else if (res.reason === 'daily_hard_cap') {
                setToast(t('voice.dailyHardCap', 'Daily voice limit reached. Try again tomorrow.'));
            } else if (res.reason === 'insufficient_coins') {
                setToast(t('voice.insufficientCoins', 'Out of L coins for extra voice turns.'));
            } else {
                setToast(t('voice.sendFailed', 'Couldn\'t send that — please retry.'));
            }
            return;
        }
        if (res.classification === 'non_english') {
            setToast(t('voice.detectedNonEnglish', 'Detected non-English — try saying it in English next time.'));
        } else if (res.charge === 'coin' && typeof res.coinsCharged === 'number') {
            setToast(t('voice.coinCharged', 'Used {{n}} L coins (over daily free limit)', { n: res.coinsCharged }));
        }
    };

    const remainingFree = session.quota?.remainingFree ?? null;
    const showFreeWarning = remainingFree !== null && remainingFree <= 5 && remainingFree > 0;
    const usingCoins = session.quota && session.quota.remainingFree === 0;

    return (
        <div
            className="border-t border-gray-200 bg-white flex flex-col"
            style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
            data-testid="voice-chat-input"
        >
            {(showFreeWarning || usingCoins) && (
                <div className="px-4 pt-2">
                    <div
                        className={`
                            px-3 py-1.5 rounded-full inline-flex items-center gap-2 text-xs font-medium
                            ${usingCoins ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}
                        `}
                        data-testid="voice-quota-chip"
                    >
                        {usingCoins
                            ? t('voice.coinChip', '−{{n}} L per turn · balance {{b}}', {
                                n: session.quota?.coinsPerExtraTurn ?? 5,
                                b: session.quota?.coinBalance ?? 0,
                            })
                            : t('voice.freeRemaining', 'Today {{used}}/{{limit}}', {
                                used: session.quota?.used ?? 0,
                                limit: session.quota?.freeLimit ?? 20,
                            })}
                    </div>
                </div>
            )}

            <PTTButton
                recorder={recorder}
                onComplete={handleComplete}
                onError={(msg) => setToast(`${t('voice.error', 'Voice error')}: ${msg}`)}
                disabled={session.uploadState === 'uploading'}
            />

            {toast && (
                <div
                    role="status"
                    className="mx-4 mb-2 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm text-center"
                    data-testid="voice-toast"
                >
                    {toast}
                </div>
            )}
        </div>
    );
};

export default VoiceChatInput;
